/**
 * Metadados VISUAIS dos nós da Agenda nas automações (issue #159).
 *
 * Os campos e rótulos vêm do catálogo do backend (api/automations/agenda_nodes.py);
 * aqui ficam só o ícone/tom e o resumo de uma linha do card. Separado de
 * nodeMeta.js (que importa este arquivo) para não misturar com os demais módulos.
 */

export const AGENDA_NODE_META = {
  'trigger.event_created': { icon: 'calendar' },
  'trigger.event_completed': { icon: 'ticks' },
  'trigger.event_overdue': { icon: 'alert' },
  'trigger.event_rescheduled': { icon: 'refresh' },
  'trigger.event_deleted': { icon: 'trash' },
  'trigger.event_edited': { icon: 'edit' },
  'trigger.event_owner_changed': { icon: 'user' },
  'trigger.event_participant_added': { icon: 'users' },
  'trigger.event_participant_removed': { icon: 'users' },
  'trigger.event_participant_responded': { icon: 'check' },
  'trigger.event_starting': { icon: 'bell' },
  'trigger.event_ended': { icon: 'clock' },
  'trigger.event_no_show': { icon: 'alert' },
  'trigger.event_imported': { icon: 'calendar' },
  'trigger.event_google_changed': { icon: 'refresh' },
  'trigger.gcal_connection_changed': { icon: 'link' },
  'trigger.gcal_sync_failed': { icon: 'alert' },
  'action.complete_event': { icon: 'ticks' },
  'action.reschedule_event': { icon: 'refresh' },
  'action.update_event': { icon: 'edit' },
  'action.cancel_event': { icon: 'trash' },
  'action.change_event_owner': { icon: 'user' },
  'action.event_participants': { icon: 'users' },
  'action.sync_google_calendar': { icon: 'refresh', tone: 'slate' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const ANTES = { 15: '15 min', 30: '30 min', 60: '1 hora', 1440: '1 dia' }
const RESPOSTA = { aceito: 'aceitou', recusado: 'recusou', talvez: 'marcou talvez' }
const CAMPO = {
  titulo: 'título', local: 'local', link_reuniao: 'link', prioridade: 'prioridade',
  contato: 'lead', negocio: 'negócio', projeto: 'projeto',
}

/** Sufixo de filtro comum dos gatilhos de evento: " · reunião · alta". */
function filtros(config) {
  const partes = [config.tipo, config.prioridade].filter(Boolean)
  return partes.length ? ` · ${partes.join(' · ')}` : ''
}

/**
 * Resumo de uma linha dos nós da Agenda. Devolve `undefined` para tipos que não
 * são da Agenda (quem chama segue para o resumo dos demais nós).
 */
export function summarizeAgenda(key, config = {}, names = {}) {
  const nome = (id) => names[id] || null
  switch (key) {
    case 'trigger.event_created':
      return `Evento criado${filtros(config)}`
    case 'trigger.event_completed':
      return `Evento concluído${filtros(config)}`
    case 'trigger.event_overdue':
      return `Evento atrasado${filtros(config)}`
    case 'trigger.event_rescheduled':
      return `Evento reagendado${filtros(config)}`
    case 'trigger.event_deleted':
      return `Evento cancelado/excluído${filtros(config)}`
    case 'trigger.event_edited':
      return config.field ? `Mudou ${CAMPO[config.field] || config.field}${filtros(config)}` : `Evento editado${filtros(config)}`
    case 'trigger.event_owner_changed':
      return config.user_id ? `Passou para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Qualquer mudança de responsável'
    case 'trigger.event_participant_added':
      return config.user_id ? `${nome(config.user_id) || 'Pessoa'} adicionada` : 'Qualquer participante adicionado'
    case 'trigger.event_participant_removed':
      return config.user_id ? `${nome(config.user_id) || 'Pessoa'} removida` : 'Qualquer participante removido'
    case 'trigger.event_participant_responded':
      return config.resposta ? `Participante ${RESPOSTA[config.resposta]}` : 'Qualquer resposta ao convite'
    case 'trigger.event_starting':
      return config.minutes ? `${ANTES[config.minutes] || `${config.minutes} min`} antes do início${filtros(config)}` : 'Escolha quanto tempo antes'
    case 'trigger.event_ended':
      return `Evento terminou${filtros(config)}`
    case 'trigger.event_no_show':
      return config.minutes != null && config.minutes !== ''
        ? `Pendente ${config.minutes} min após o fim${filtros(config)}` : 'Informe os minutos após o fim'
    case 'trigger.event_imported':
      return 'Evento novo vindo do Google'
    case 'trigger.event_google_changed':
      return config.change === 'cancelado' ? 'Evento cancelado no Google'
        : config.change === 'alterado' ? 'Evento alterado no Google' : 'Evento alterado ou cancelado no Google'
    case 'trigger.gcal_connection_changed':
      return config.state === 'desconectado' ? 'Google Calendar desconectado'
        : config.state === 'conectado' ? 'Google Calendar conectado' : 'Google Calendar conectado/desconectado'
    case 'trigger.gcal_sync_failed':
      return 'Falha ao sincronizar com o Google'
    case 'action.complete_event':
      return 'Marca o evento como concluído'
    case 'action.reschedule_event': {
      if (config.mode === 'fixed') {
        return config.date ? `Para ${trunc(config.date, 16)}${config.hora ? ` ${config.hora}` : ''}` : 'Defina a nova data'
      }
      if (config.mode === 'shift' && config.amount) {
        const un = { minutes: 'min', hours: 'h', days: 'dia(s)' }[config.unit || 'hours']
        return `${config.direction === 'earlier' ? 'Adianta' : 'Adia'} ${config.amount} ${un}`
      }
      return 'Escolha como reagendar'
    }
    case 'action.update_event': {
      const campos = ['titulo', 'tipo', 'prioridade', 'local', 'link_reuniao', 'descricao']
        .filter((c) => config[c]).map((c) => CAMPO[c] || c)
      return campos.length ? `Altera: ${campos.join(', ')}` : 'Escolha o que alterar'
    }
    case 'action.cancel_event':
      return 'Exclui o evento da Agenda'
    case 'action.change_event_owner':
      return config.user_id ? `Para ${nome(config.user_id) || 'pessoa selecionada'}` : 'Escolha o novo responsável'
    case 'action.event_participants': {
      if (!config.operation) return 'Escolha adicionar ou remover'
      const quem = config.who === 'lead' ? 'o lead' : (nome(config.user_id) || 'pessoa da equipe')
      return `${config.operation === 'remove' ? 'Remove' : 'Adiciona'} ${quem}`
    }
    case 'action.sync_google_calendar':
      return 'Sincroniza o Google Calendar agora'
    default:
      return undefined
  }
}
